import React from "react";
import { Modal } from "react-bootstrap";
import { Button } from "@mui/material";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import * as moment from "moment";
import "../../assets/scss/tables.scss";

/* skills arrive as one comma-separated string */
export const splitSkills = (value) =>
    String(value || "")
        .split(",")
        .map((skill) => skill.trim())
        .filter(Boolean);

/* The description is stored as HTML from the editor. These pull a readable
   snippet out of it for the table, without rendering the markup. */
export const plainText = (html) => String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

export const excerpt = (html, limit = 90) => {
    const text = plainText(html);
    return text.length > limit ? `${text.slice(0, limit).trimEnd()}…` : text;
};

const years = (value) => {
    if (value === "" || value === null || value === undefined) return "--";
    return `${value} ${Number(value) > 1 ? "Years" : "Year"}`;
};

const Fact = ({ icon, label, value }) => (
    <div className="wh-fact">
        <span className="wh-fact-icon">{icon}</span>
        <div className="wh-fact-text">
            <span className="wh-fact-label">{label}</span>
            <span className="wh-fact-value">{value}</span>
        </div>
    </div>
);

const JobDescriptionModal = ({ job, onClose }) => {

    if (!job) return null;

    const expired = moment(job.expiredOn).isBefore(new Date(), "day");
    const skills = splitSkills(job.skills);

    return (
        <Modal size="lg" show={Boolean(job)} onHide={onClose} className="wh-modal">
            <Modal.Header closeButton>
                <Modal.Title>{job.jobTitle || "Job Description"}</Modal.Title>
            </Modal.Header>

            <Modal.Body>

                <div className="wh-profile-hero">
                    <div className="wh-avatar"><WorkOutlineOutlinedIcon /></div>
                    <div className="wh-profile-id">
                        <h5>{job.jobTitle || "--"}</h5>
                        <p>{years(job.experienceFrom)} to {years(job.experienceTo)} experience</p>
                    </div>
                    <span className={`wh-pill ${expired ? "wh-pill-danger" : "wh-pill-good"}`}>
                        {expired ? "Inactive" : "Active"}
                    </span>
                </div>

                <div className="wh-facts-grid">
                    <Fact icon={<TimelineOutlinedIcon />} label="Experience"
                        value={`${job.experienceFrom ?? "--"} – ${years(job.experienceTo)}`} />
                    <Fact icon={<GroupsOutlinedIcon />} label="Interview Rounds"
                        value={job.numberOfRounds || "--"} />
                    <Fact icon={<EventBusyOutlinedIcon />} label="Expires On"
                        value={job.expiredOn ? moment(job.expiredOn).format("DD MMM YYYY") : "--"} />
                </div>

                <h6 className="wh-section-title"><PsychologyOutlinedIcon />Skills</h6>
                {skills.length > 0
                    ? <div className="wh-pills">
                        {skills.map((skill, i) => <span className="wh-pill" key={i}>{skill}</span>)}
                    </div>
                    : <p className="wh-section-empty">No skills listed for this job.</p>}

                <h6 className="wh-section-title"><DescriptionOutlinedIcon />Requirements</h6>
                {job.requirements
                    ? <div className="wh-rich-text" dangerouslySetInnerHTML={{ __html: job.requirements }} />
                    : <p className="wh-section-empty">No job description has been written yet.</p>}

            </Modal.Body>

            <Modal.Footer>
                <Button variant="contained" color="inherit" onClick={onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default JobDescriptionModal;
